import React, { useState } from 'react';
import { Button, Empty } from 'antd';
import { TitleWithActions, CustomPagination } from 'components/components';
import Input from 'components/components/Input';
import SubTab from 'components/components/SubTab';
import RequestRowContent from 'components/RequestsOverview/components/RequestRow/Content';
import { STATUS_COLOR_CLASS } from 'components/RequestsOverview/components/RequestDetailsDrawer/utils/constants';
import moment from 'moment';
import { formatSlashMDYHD } from 'components/utils/constants/formatters';
import { usePortalPortfolio, usePortalRequests, usePortalScope } from 'packages/venture_builder_repository/portal/hooks';
import { PortalRequest, PortalScope } from 'packages/venture_builder_repository/portal/types';
import { requestStatusLabels } from 'packages/venture_builder_repository/portal/utils';
import { NUMBER_TO_REQUEST_STATUS_MAP } from 'packages/service_request_repository/utils/constants';
import DraftRequestDrawer from './components/DraftRequestDrawer/View';
import SentRequestDrawer from './components/SentRequestDrawer/View';
import {
  EmptyPortfolio, LoadError, Loading, money, Refresh, startupLabel,
} from './shared';
import '../RequestsOverview/components/RequestOverviewTabs/RequestOverviewTabs.scss';
import '../RequestsOverview/components/RequestDetailsDrawer/components/OverviewTab/OverviewTab.scss';
import './VentureBuilderPortal.scss';

const tabLabels: Record<number, string> = { ...requestStatusLabels, 1: 'Unsent' };

export const RequestDrawer = ({ request, ...props }: {
  scope: PortalScope, request: PortalRequest, onClose: () => void,
}) => (
  request.status === 1
    ? <DraftRequestDrawer request={request} scope={props.scope} onClose={props.onClose} />
    : <SentRequestDrawer request={request} scope={props.scope} onClose={props.onClose} />
);

const VentureBuilderRequests = () => {
  const scope = usePortalScope();
  const portfolio = usePortalPortfolio(scope);
  const queries = usePortalRequests(scope, portfolio.error ? [] : portfolio.data || []);
  const [openedId, setOpenedId] = useState<number>();
  const [startupId, setStartupId] = useState<number | 'all'>('all');
  const [status, setStatus] = useState<number | 'all'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const requests = queries.flatMap((query) => query.data || []).sort((a, b) => b.id - a.id);
  const error = portfolio.error || queries.find((query) => query.error)?.error;
  const loading = portfolio.isPending || queries.some((query) => query.isPending);
  const opened = requests.find((request) => request.id === openedId);
  const refresh = () => { portfolio.refetch(); queries.forEach((query) => query.refetch()); };
  const scoped = requests.filter((request) => startupId === 'all' || request.startup.id === startupId);
  const filtered = scoped.filter((request) => status === 'all' || request.status === status);
  const page = Math.max(1, Math.min(currentPage, Math.ceil(filtered.length / limit)));
  const statuses: (number | 'all')[] = ['all', ...Object.keys(requestStatusLabels).map(Number)];
  return (
    <div className="content-container requests-overview vb-portal">
      <TitleWithActions title="Requests" subtitle="View and manage all your requests." size="l">
        <Refresh onClick={refresh} loading={portfolio.isFetching || queries.some((query) => query.isFetching)} />
      </TitleWithActions>
      {error && <LoadError error={error} retry={refresh} />}
      {!error && portfolio.data?.length === 0 && <EmptyPortfolio />}
      {!error && portfolio.data?.length !== 0 && (
        <>
          <Input label="Startup" layout="vertical" className="vb-portal__search">
            <Input.Select
              aria-label="Filter requests by startup"
              showSearch
              value={startupId}
              onChange={(value) => { setStartupId(value); setCurrentPage(1); }}
              options={[
                { value: 'all', label: 'All startups' },
                ...(portfolio.data || []).map((startup) => ({ value: startup.id, label: startupLabel(startup) })),
              ]}
            />
          </Input>
          <div className="request-overview-tabs">
            {statuses.map((value) => (
              <SubTab
                key={value}
                activeKey={String(status)}
                tabKey={String(value)}
                tab={`${value === 'all' ? 'All' : tabLabels[value]} (${scoped.filter((request) => (
                  value === 'all' || request.status === value
                )).length})`}
                onClick={() => { setStatus(value); setCurrentPage(1); }}
              />
            ))}
          </div>
          <div className="requests-container">
            {loading ? <Loading /> : filtered.slice((page - 1) * limit, page * limit).map((request) => (
              <RequestRowContent
                key={request.id}
                requestId={request.id}
                requestTitle={request.itemList || request.serviceProvider?.name || `Request #${request.id}`}
                requestStatus={requestStatusLabels[request.status] || 'Unknown'}
                statusClassName={STATUS_COLOR_CLASS()[NUMBER_TO_REQUEST_STATUS_MAP[request.status] || 'rejected']}
                rowDataLeft={`From ${startupLabel(request.startup)}`}
                rowDataRight={`${request.serviceProvider?.name || 'Service provider unavailable'} · ${money(request.totalPrice)}`}
                date={request.applicationDate ? moment(request.applicationDate).format(formatSlashMDYHD) : 'Date unavailable'}
                logos={[{ key: 'provider', src: request.serviceProvider?.logo }]}
                actions={(
                  <Button className="secondary-button" onClick={() => setOpenedId(request.id)}>View details</Button>
                )}
              />
            ))}
            {!loading && !filtered.length && <Empty description="No requests found" />}
          </div>
          {!!filtered.length && !loading && (
            <CustomPagination
              currentPage={page}
              limit={limit}
              total={filtered.length}
              setCurrentPage={setCurrentPage}
              setLimit={(value) => { setLimit(value); setCurrentPage(1); }}
            />
          )}
        </>
      )}
      {!error && opened && <RequestDrawer scope={scope} request={opened} onClose={() => setOpenedId(undefined)} />}
    </div>
  );
};

export default VentureBuilderRequests;
